/**
 * SALA_ABAS — registro de abas reais por sala, usado pela SidebarNova
 * pro balão que aparece no hover (mostra as abas e deixa entrar direto
 * numa delas via `?aba=slug`).
 *
 * Cada `slug` aqui precisa bater EXATAMENTE com o valor que o
 * `useState(new URLSearchParams(location.search).get('aba') || '...')`
 * de cada Sala*.jsx espera (o mesmo valor usado nas comparações
 * `aba === 'slug'` e nos `setAba('slug')` daquela sala). Todas as
 * entradas abaixo foram conferidas contra o código real de cada
 * arquivo em 29-30/07/2026 — não são um palpite.
 *
 * Deixadas de fora de propósito (mesmo quando a sala tem a aba):
 *  - Abas-ação, tipo "criar novo registro" (ex.: 'nova', 'novo') — não
 *    são um destino pra "ver algo", são um formulário em branco.
 *  - Abas condicionadas a um registro específico já selecionado (ex.:
 *    'detalhe' em Sala21/Sala24) — abrir direto nelas via link não tem
 *    o quê mostrar, já que o estado que elas dependem não existe ainda.
 *  - Abas atrás de permissão de gerenciar (ex.: 'config' em Sala8/
 *    Sala12/Sala17/Sala30/Sala39, 'tipos' na NC) — a sidebar não sabe
 *    permissão por aba, só por sala; ficou de fora pra não linkar pra
 *    algo que a pessoa talvez não possa ver.
 *
 * Sala sem entrada aqui: o balão simplesmente não aparece no hover
 * (a SidebarNova já trata isso) — clicar continua abrindo normal, na
 * aba padrão de cada uma. Adicionar uma sala nova aqui é sempre
 * seguro: só cria o balão, nunca tira a navegação direta que já existe.
 *
 * Pendência conhecida: nenhuma — as duas salas que faltavam (22 e 23) foram
 * investigadas e excluídas por um motivo concreto, não por falta de tempo:
 * ver nota no fim do arquivo.
 */
export const SALA_ABAS = {
  // Sala 2 — Inspeção de Produtos (era Assistências Internas até 16/08/2026)
  // Sala 2 — Inspeção de Produtos (era Assistências Internas até a
  // renumeração de 16/08/2026, quando a antiga Sala 42 virou esta).
  // As abas 'historico' e 'para_mim' eram das Assistências e ficaram
  // aqui até 23/08 — o balão oferecia dois destinos que a tela não
  // sabia renderizar. 'nova' fica de fora por ser aba-ação.
  /* ⚠ SEM ABAS DESDE 20/09/2026: a sala virou uma vista só, no molde da
     Sala 1. 'geral' e 'inspecoes' viraram o corpo; 'politica' foi para a
     Sala 6 (quem DEFINE); 'posto' é sobre-tela, não destino. Balão que
     oferece aba inexistente manda a pessoa para lugar nenhum. */
  /* ⚠ ARRAY VAZIO EM DUAS LINHAS, de propósito. `2: []` numa linha só fez
     o portão `coerencia_sala` acusar três abas da Sala 5 como sendo desta:
     a regex dele casa `N: [` até o próximo `]` em linha própria, e sem
     quebra o casamento pula para o fecho da entrada seguinte. Todas as
     outras entradas são multilinha — a minha era a exceção. */
  2: [
    { slug: 'inspecoes', label: 'Inspeções' },
    { slug: 'atencao',   label: 'Com atenção' },
  ],
  5: [
    // (V1931.27) a sala abre na planilha das assistências; a Produção saiu (a planilha é ela) — as abas da tela, na mesma ordem
    { slug: 'assist',       label: 'Assistências' },
    { slug: 'recebimento',  label: 'Recebimento' },
    { slug: 'esperando',    label: 'Aguardando' },
    { slug: 'registro',     label: 'Histórico' },
  ],
  // Sala 6 — Manual de Produtos
  /* ⚠⚠ TRÊS ABAS DESDE 18/09/2026, organizadas por QUEM CHEGA: quem
     quer saber de uma peça, quem vai formar alguém, quem mantém.
     `Visão Geral` e `Todas as Fichas` saíram — respondiam "quantas
     fichas estão em rascunho", que o propósito da sala diz não ser
     pergunta dela. */
  /* ⚠ 'fichas' saiu em 20/09/2026 com a tabela; 'conferencia' tomou o
     lugar. Configurações ganhou Política, vinda da Sala 2. */
  6: [
    { slug: 'produtos',    label: 'Produtos' },
    { slug: 'comuns',      label: 'Requisitos comuns' },
    { slug: 'config',      label: 'Configurações' },
  ],
  // A aba Expedição (25/08/2026) fica DENTRO do produto, não no nível da
  // sala — por isso não entra aqui. A sidebar lista as abas da sala; as
  // do produto aberto são outro nível.
  // Sala 42 — Inspeção de Produtos (nova em 31/07/2026, separada da
  // Sala 6 — quem desenha o processo não é sempre quem executa a
  // inspeção). 'qr' (QR de Posto) deixada de fora — só existe pra
  // quem tem Gerenciar, e a sidebar não sabe permissão por aba.
  // Sala 42 — Configuração da Expedição. O número foi reaproveitado em
  // 17/08/2026; 'geral' e 'inspecoes' eram da Inspeção que virou Sala 2.
  // A aba "Modelo de Checklist" saiu em 25/08/2026: o que ela decidia
  // (formato e mínimos) virou a engrenagem de Ajustes, porque é regra de
  // funcionamento e não trabalho.
  42: [
    { slug: 'biblioteca',  label: 'Biblioteca de Itens' },
    { slug: 'aprovadores', label: 'Aprovação da Qualidade' },
  ],
  // Sala 8 — Gestão 5S
  8: [
    { slug: 'auditorias', label: 'Auditorias' },
    { slug: 'ranking',    label: 'Ranking' },
    { slug: 'cronograma', label: 'Cronograma' },
  ],
  // Sala 9 — Treinamentos
  9: [
    { slug: 'geral',        label: 'Visão Geral' },
    { slug: 'treinamentos', label: 'Treinamentos' },
    { slug: 'credenciais',  label: 'Credenciais' },
    { slug: 'tipos',        label: 'Tipos' },
  ],
  // Sala 10 — Causa Raiz
  10: [
    { slug: 'origens',  label: 'De onde veio' },
    { slug: 'verifica', label: 'Quem verifica' },
    { slug: 'regras',   label: 'As regras' },
  ],
  // Sala 12 — Inspeção IQF
  12: [
    { slug: 'historico', label: 'Histórico' },
    { slug: 'dashboard', label: 'Dashboard' },
  ],
  // Sala 15 — Rastreabilidade. A aba "dossies" fica FORA de propósito:
  // é atrás de permissão de gerenciar, e a sidebar não sabe permissão
  // por aba, só por sala (política no topo deste arquivo).
  15: [
    { slug: 'rastrear', label: 'Rastrear' },
  ],
  // Sala 16 — SAC
  16: [
    { slug: 'lista',       label: 'Atendimentos' },
    { slug: 'indicadores', label: 'Indicadores' },
    { slug: 'listas',      label: 'Listas' },
  ],
  // Sala 17 — Lançamento de Produtos
  17: [
    { slug: 'lista',   label: 'Lançamentos' },
    { slug: 'quadro',  label: 'Quadro' },
  ],
  // Sala 18 — Melhoria de Produtos
  18: [   // (V1915) a sala no v2: as cinco abas da demo aprovada; a configuração mora no ⋯ da sala
    { slug: 'melhorias',   label: 'Melhorias' },
    { slug: 'ensaios',     label: 'Ensaios' },
    { slug: 'produtos',    label: 'Produtos' },
    { slug: 'pauta',       label: 'Pauta' },
    { slug: 'indicadores', label: 'Indicadores' },
  ],
  // Sala 19 — Diário de Bordo (V1909)
  19: [
    { slug: 'hoje',      label: 'Hoje' },
    { slug: 'trip',      label: 'Tripulação' },
    { slug: 'melhorias', label: 'Melhorias' },
    { slug: 'diario',    label: 'Diário' },
  ],
  // Sala 20 — Não Conformidades (componente NC.jsx)
  20: [
    { slug: 'geral', label: 'Visão geral' },
    { slug: 'xr',    label: 'Carta X̄/R' },
    { slug: 'hist',  label: 'Histograma' },
    { slug: 'disp',  label: 'Dispersão' },
    { slug: 'var',   label: 'Variação' },
  ],
  // Sala 21 — Colaboradores
  21: [
    { slug: 'lista',           label: 'Colaboradores' },
    { slug: 'vencimentos',     label: 'Vencimento de ASOs' },
    { slug: 'vencCredenciais', label: 'Vencimento de Credenciais' },
    { slug: 'ordensServico',   label: 'Ordens de Serviço' },
    { slug: 'historico',       label: 'Histórico de Pessoal' },
  ],
  // Sala 24 — Acidentes e Incidentes
  24: [
    { slug: 'lista',            label: 'Registros' },
    { slug: 'diasSemAcidente',  label: 'Dias Sem Acidentes' },
    { slug: 'taxaFG',           label: 'Taxa de Frequência e Gravidade' },
  ],
  // Sala 25 — Controle de EPIs
  25: [
    { slug: 'catalogo',  label: 'Catálogo de EPIs' },
    { slug: 'auditoria', label: 'Auditoria Mensal' },
  ],
  // Sala 26 — Preventivos
  26: [
    { slug: 'extintores',  label: 'Extintores' },
    { slug: 'hidrantes',   label: 'Hidrantes' },
    { slug: 'motobombas',  label: 'Motobombas' },
    { slug: 'documentos',  label: 'Documentos da Empresa' },
  ],
  // Sala 27 — APR e Permissão de Trabalho
  27: [
    { slug: 'pt',        label: 'Permissão de Trabalho' },
    { slug: 'ar',        label: 'Análise de Riscos (AR)' },
    { slug: 'registros', label: 'Buscar Registros' },
  ],
  // Sala 30 — Gestão de Terceiros
  30: [
    { slug: 'visitas',       label: 'Visitas' },
    { slug: 'terceiros',     label: 'Empresas Terceiras' },
    { slug: 'certificacoes', label: 'Certificações' },
  ],
  // Sala 32 — Configuração de AT & All Care (26/09/2026; era Máquinas, nunca usada)
  32: [   // (V1904, a biblioteca nova) os nomes das abas como na tela; "Grupos de produto" virou "As famílias"
    { slug: 'problema', label: 'Problemas' },   // (V1921) as Partes e As famílias foram para a Sala 33
    { slug: 'listas',   label: 'Listas' },
    { slug: 'assinam',  label: 'Quem assina' },
  ],
  // Sala 33 — Produtos
  33: [
    { slug: 'produtos',  label: 'Produtos' },
    { slug: 'estrutura', label: 'Estrutura' },
    { slug: 'partes',    label: 'Partes' },   // (V1921)
  ],
  // Sala 35 — Estudo de Viabilidade
  34: [
    { slug: 'biblioteca', label: 'Biblioteca' },
    { slug: 'padrao',     label: 'Padrão dos postos' },
    { slug: 'produto',    label: 'Por produto' },
    { slug: 'atencao',    label: 'Atenção' },
    { slug: 'base',       label: 'Postos' },
  ],
  35: [
    { slug: 'lista',  label: 'Solicitações' },
    { slug: 'config', label: 'Configurar Categorias' },
  ],
  // Sala 36 — Engenharia de Produtos (V1914, 18/10/2026; era a Requisição de Compras)
  36: [
    { slug: 'acervo',    label: 'Acervo' },
    { slug: 'registros', label: 'Registros' },
    { slug: 'metas',     label: 'Metas' },
    { slug: 'equipe',    label: 'Equipe' },
  ],
  // Sala 37 — Indicadores de Expedição
  // 'checklist' saiu em 23/08: configurar modelo passou a ser da Sala 42
  // (17/08) e esta tela não renderiza mais essa aba.
  37: [
    /* 25/09/2026: a lógica nova — três perguntas. Os slugs antigos
       (dashboard, historico, gerencial) continuam caindo na aba nova
       equivalente, pela tabela `ABA_ANTIGA` da Sala37.jsx. */
    { slug: 'movimento', label: 'Movimento' },
    { slug: 'registros', label: 'Registros' },
    { slug: 'qualidade', label: 'Qualidade' },
  ],
  // Sala 38 — Indicadores de Assistências Internas
  // Reescrita em 23/08 a partir do que a tela renderiza: as três antigas
  // ficaram de quando a Sala 38 media Assistências Internas.
  /* ⚠ Rótulos conferidos na tela, não supostos (20/09/2026): 'numeros'
     aparece como "Por produto". 'gerencial' fica de fora — só existe para
     quem tem Gerenciar, e a sidebar não sabe permissão por aba, mesma
     razão do 'qr' da antiga Sala 42. */

  // Sala 40 — Indicadores de Planos de Ação
  40: [
    { slug: 'geral',        label: 'Visão geral' },
    { slug: 'trava',        label: 'Onde trava' },
    { slug: 'reincidencia', label: 'Reincidência' },
  ],
  // Sala 39 — Indicadores de AT & All Care
  39: [
    { slug: 'geral',     label: 'Visão geral' },
    { slug: 'ocorrencias', label: 'Ocorrências' },
    { slug: 'origem',    label: 'Origem' },
  ],
};

/**
 * Investigado e EXCLUÍDO de propósito (30/07/2026) — Sala 22 (CIPA) e
 * Sala 23 (Brigada de Incêndio):
 *
 * Nas outras 26 salas, `aba` é o nível mais alto — sempre disponível
 * assim que a página monta, com um valor padrão fixo. Nessas duas não:
 * as abas (`aba` na CIPA; `abaEquipe` na Brigada) só fazem sentido
 * DEPOIS que a pessoa escolhe uma empresa e um mandato/equipe
 * específicos (`mandatoSel`/`equipeSel`) — e essa escolha é feita
 * clicando numa lista carregada em tempo real, nunca por um slug fixo
 * (o "id" é da empresa/mandato no banco, não um nome memorizável tipo
 * 'fichas' ou 'produtos'). Um link `?aba=sipat` chegaria à sala sem
 * nenhum mandato selecionado — a aba tentaria renderizar sem o dado
 * que ela depende (`mandatoSel.id`), e ou quebra ou fica em branco.
 *
 * Resolver isso direito exigiria decidir uma empresa/mandato "padrão"
 * pra carregar sozinho (ex.: o mandato mais recente ativo) — uma
 * mudança de comportamento real da sala, não só do link. Fica como
 * ideia registrada, não implementada: perguntar ao Juan se faz
 * sentido esse "auto-carregar o mandato mais recente" antes de mexer.
 */
